import { SdkError } from '../core/sdk-error'
import type { AxiosInstance } from 'axios'
import type { AuthActions, AuthData, B24OAuthParams, B24OAuthSecret, CallbackRefreshAuth, CustomRefreshAuth, HandlerRefreshAuth, TypeDescriptionError, TypeDescriptionErrorV3 } from '../types/auth'
import type { TypeHttp } from '../types/http'
import axios, { AxiosError } from 'axios'
import { RefreshTokenError } from './refresh-token-error'
import { Type } from '../tools/type'
import { EnumAppStatus } from '../types/b24-helper'
import { ApiVersion } from '../types/b24'

/**
 * OAuth Authorization Manager
 *
 * @link https://apidocs.bitrix24.com/settings/oauth/index.html
 * @link https://bitrix24.github.io/b24jssdk/docs/oauth/
 */
export class AuthOAuthManager implements AuthActions {
  #clientAxios: AxiosInstance
  #callbackRefreshAuth: null | CallbackRefreshAuth = null
  #customRefreshAuth: null | CustomRefreshAuth = null
  #authOptions: B24OAuthParams
  readonly #oAuthSecret: B24OAuthSecret
  #authExpires: number = 0
  #authExpiresIn: number = 0
  readonly #domain: string
  readonly #b24TargetRest: string
  readonly #b24Target: string
  readonly #b24TargetRestWithPath: Map<ApiVersion, string>
  readonly #oAuthTarget: string // from serverEndpoint, e.g. 'https://oauth.bitrix24.tech'

  #isAdmin: null | boolean = null

  constructor(
    b24OAuthParams: B24OAuthParams,
    oAuthSecret: B24OAuthSecret
  ) {
    this.#authOptions = Object.assign({}, b24OAuthParams) as B24OAuthParams
    this.#oAuthSecret = Object.freeze(Object.assign({}, oAuthSecret)) as B24OAuthSecret

    this.#domain = this.#authOptions.domain
      .replaceAll('https://', '')
      .replaceAll('http://', '')
      .replace(/:(80|443)$/, '')

    // `client_endpoint` arrives from the portal **with** a trailing slash —
    // `https://<portal>/rest/` — and every consumer of this value appends its
    // own separator, so keeping it produced a double slash on both versions:
    // `…/rest//profile` on v2 and `…/rest//api/batch` on v3. The portal tolerates
    // it (measured: identical answers either way), so nothing was broken — but it
    // reaches access logs, proxy path rules and anything matching on the URL, and
    // it is one character to stop emitting.
    //
    // Normalised here rather than at each use so the two derived values below,
    // and both entries of the version map, are built from one shape.
    this.#b24TargetRest = this.#authOptions.clientEndpoint.replace(/\/+$/, '')
    // Anchored, and reading a value that no longer ends in a slash. The previous
    // `replace('/rest/', '')` matched only the trailing-slash spelling: had the
    // portal ever sent `client_endpoint` without one, `getTargetOrigin()` would
    // have returned `https://<portal>/rest` — the REST root where the caller
    // asked for the portal.
    this.#b24Target = this.#b24TargetRest.replace(/\/rest$/, '')
    // Same shape, same reasoning as `clientEndpoint` above: strip any trailing
    // slash first, then take the REST root off the end with an anchored match,
    // so both spellings of the input give the same origin.
    this.#oAuthTarget = this.#authOptions.serverEndpoint.replace(/\/+$/, '').replace(/\/rest$/, '')
    this.#authExpires = this.#authOptions.expires * 1_000
    this.#authExpiresIn = this.#authOptions.expiresIn

    // No default Content-Type: the only request on this client (token refresh)
    // sets its own `application/x-www-form-urlencoded` header per call (#149).
    this.#clientAxios = axios.create({
      baseURL: this.#oAuthTarget
    })

    this.#b24TargetRestWithPath = new Map()
    this.#b24TargetRestWithPath.set(ApiVersion.v2, `${this.#b24TargetRest}`)
    this.#b24TargetRestWithPath.set(ApiVersion.v3, `${this.#b24TargetRest}/api`)
  }

  /**
   * Returns authorization data
   * @see Http.#prepareParams
   */
  public getAuthData(): false | AuthData {
    return this.#authExpires > Date.now()
      ? ({
          access_token: this.#authOptions.accessToken,
          refresh_token: this.#authOptions.refreshToken,
          expires: this.#authExpires / 1_000,
          expires_in: this.#authExpiresIn,
          domain: this.#domain,
          member_id: this.#authOptions.memberId
        } as AuthData)
      : false
  }

  // region RefreshAuth ////
  /**
   * Updates authorization data
   */
  public async refreshAuth(): Promise<AuthData> {
    try {
      let payload: undefined | HandlerRefreshAuth = undefined

      if (this.#customRefreshAuth) {
        payload = await this.#customRefreshAuth()
      } else {
        /**
         * Send the refresh request as an `application/x-www-form-urlencoded`
         * POST body rather than as GET query params, so `client_secret` and
         * `refresh_token` never appear in the request URL — where they would leak
         * via proxy / CDN / server access logs (which routinely record full URLs
         * with their query strings). The auth server (the portal's
         * `serverEndpoint`) accepts the params from the body — the Bitrix docs
         * document only the GET-with-query form, so this was verified empirically
         * against the current host `oauth.bitrix24.tech`. (#149)
         */
        const body = new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: this.#oAuthSecret.clientId,
          client_secret: this.#oAuthSecret.clientSecret,
          refresh_token: this.#authOptions.refreshToken
        })

        const response = await this.#clientAxios.post(
          '/oauth/token/',
          body,
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
        )

        if (response.data.error) {
          throw new SdkError({ code: 'JSSDK_OAUTH_TOKEN_REFRESH_FAILED', description: `Token update error: ${response.data.error}`, status: 0 })
        }
        if (response.status !== 200) {
          throw new SdkError({ code: 'JSSDK_OAUTH_TOKEN_REFRESH_BAD_STATUS', description: `Token update error status code: ${response.status}`, status: response.status })
        }

        /**
         * @memo domain = e.g. 'oauth.bitrix24.tech'
         */
        payload = response.data
      }

      if (!payload) {
        throw new SdkError({ code: 'JSSDK_OAUTH_TOKEN_REFRESH_NO_DATA', description: 'Unable to obtain authorization update data', status: 0 })
      }

      this.#authOptions.accessToken = payload.access_token
      this.#authOptions.refreshToken = payload.refresh_token
      this.#authOptions.expires = Number.parseInt(payload.expires || '0')
      this.#authOptions.expiresIn = Number.parseInt(payload.expires_in || '3600')
      this.#authOptions.clientEndpoint = payload.client_endpoint
      this.#authOptions.serverEndpoint = payload.server_endpoint
      this.#authOptions.scope = payload.scope
      this.#authOptions.status = Object.values(EnumAppStatus).find(value => value === payload.status) || EnumAppStatus.Free

      this.#authExpires = this.#authOptions.expires * 1_000

      const authData = this.getAuthData() as AuthData

      if (this.#callbackRefreshAuth) {
        await this.#callbackRefreshAuth({ authData, b24OAuthParams: this.#authOptions })
      }

      return authData
    } catch (error) {
      if (error instanceof AxiosError) {
        const answerError = {
          code: error?.code || 0,
          description: error?.message || ''
        }

        if (
          error.response
          && error.response.data
          && !Type.isUndefined(error.response.data.error)
        ) {
          const responseData = error.response.data as TypeDescriptionError | TypeDescriptionErrorV3

          if (
            responseData.error
            && typeof responseData.error === 'object'
            && 'code' in responseData.error
          ) {
            answerError.code = responseData.error.code
            answerError.description = responseData.error.message
            if (responseData.error.validation) {
              responseData.error.validation.forEach((row) => {
                answerError.description += `${row?.message || JSON.stringify(row)}`
              })
            }
          } else if (responseData.error && typeof responseData.error === 'string') {
            answerError.code = responseData.error
            answerError.description = (responseData as TypeDescriptionError)?.error_description ?? answerError.description
          }
        }

        throw new RefreshTokenError({
          code: String(answerError.code),
          description: answerError.description,
          status: error.response?.status || 0
        })
      } else if (error instanceof Error) {
        throw error
      }

      // Both `instanceof Error` branches above have already rethrown, so `error`
      // here is always a non-Error value — carry it as the cause verbatim.
      // Deliberately a bare Error, not SdkError: this is the last-resort catch for a
      // NON-Error thrown value, and its job is to carry that value through
      // transparently as the standard `.cause` (#320, regression-tested in
      // error-cause.unit.spec.ts). SdkError exposes the original via the
      // non-enumerable `originalError`, not `.cause`, so wrapping here would drop
      // the tested `.cause` contract for no gain — a stable `.code` adds nothing
      // to a "something non-Error was thrown" fallback (#155 reasoned exception).
      // Trade-off, stated plainly: unlike SdkError.originalError, `.cause` is
      // ENUMERABLE — a property-walking serializer (Sentry et al.) WILL see the
      // carried value. This is the one throw site where the #189 non-enumerability
      // guarantee does not apply, reachable only when third-party code threw a
      // non-Error value.
      throw new Error(`Strange error: ${String(error)}`, { cause: error })
    }
  }

  public setCallbackRefreshAuth(cb: CallbackRefreshAuth): void {
    this.#callbackRefreshAuth = cb
  }

  public removeCallbackRefreshAuth(): void {
    this.#callbackRefreshAuth = null
  }

  public setCustomRefreshAuth(cb: CustomRefreshAuth): void {
    this.#customRefreshAuth = cb
  }

  public removeCustomRefreshAuth(): void {
    this.#customRefreshAuth = null
  }
  // endregion ////

  public getUniq(prefix: string): string {
    return [prefix, this.#authOptions.memberId || ''].join('_')
  }

  /**
   * @inheritDoc
   */
  public getTargetOrigin(): string {
    return `${this.#b24Target}`
  }

  /**
   * @inheritDoc
   */
  public getTargetOriginWithPath(): Map<ApiVersion, string> {
    return this.#b24TargetRestWithPath
  }

  /**
   * Determines whether the current user has administrator rights
   */
  get isAdmin(): boolean {
    if (null === this.#isAdmin) {
      throw new SdkError({ code: 'JSSDK_OAUTH_IS_ADMIN_NOT_INIT', description: 'isAdmin not init. You need call B24OAuth::initIsAdmin().', status: 0 })
    }

    return this.#isAdmin
  }

  public async initIsAdmin(http: TypeHttp, requestId?: string): Promise<void> {
    // set def value
    this.#isAdmin = false

    // region ver3 ////
    /**
     * This is just a template. When API.v3 arrives, we'll replace it.
     * @todo fix then the new API will be available
     */
    if (http.apiVersion === ApiVersion.v3) {
      const response = await http.call('profile', {}, requestId)
      if (!response.isSuccess) {
        throw new SdkError({ code: 'JSSDK_OAUTH_PROFILE_FAILED', description: response.getErrorMessages().join(';'), status: 0 })
      }

      const data: { profile: { id: number, admin: boolean } } = response.getData()!.result as any

      if (data.profile?.admin) {
        this.#isAdmin = true
      }

      return
    }
    // endregion ////

    // region ver2 ////
    const response = await http.call('profile', {}, requestId)
    if (!response.isSuccess) {
      throw new SdkError({ code: 'JSSDK_OAUTH_PROFILE_FAILED', description: response.getErrorMessages().join(';'), status: 0 })
    }

    const data: { ID: number, ADMIN: boolean } = response.getData()!.result as any

    if (data?.ADMIN) {
      this.#isAdmin = true
    }
    // endregion ////
  }
}
